import styles from './styles.module.scss';
import AfrilandLogo from '@/assets/images/logo.png';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavItem } from '@/ui-kit/NavItem';
import { DropdownNavItem } from '@/ui-kit/DropdownNavItem';
import ContextModal from '@/modal/contextModal';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import clsx from 'clsx';
import { signOutScope } from '@/utils/session';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { selectUsers, logoutUser } from '@/features/user/userSlice';
import { selectConfirmModal, setConfirmPath, setShowConfirmLeave } from '@/features/confirmModal/confirmModalSlice';
import { selectPublicSettings } from '@/features/publicSettings/publicSettingsSlice';
import Logout from '@/services/logout';
import Autologout from '@/services/autologout';
import { IconButton, Tooltip } from '@mui/material';
import { selectTabs, slicedText } from '@/utils/general';
import { useSubUserManagement } from '@/hooks/useSubUserManagement';
import Button from '@/ui-kit/Button';
import MenuIcon from '@/assets/svg/union.svg';
import CloseIcon from '@/assets/svg/close.svg';
import UserIcon from '@/assets/svg/userAvatar.svg';
import LogoutIcon from '@mui/icons-material/Logout';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { useFeatureFlags } from '@/hooks/useFeatureFlag';

export const Header = () => {
  const { t } = useTranslation();
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const { isCardFeature, isStatementsFeature, isLoansFeature, isExchangesFeature, isDepositsFeature, isSavingFeature, isCheckbookFeature } =
    useFeatureFlags();
  const { restrictSubUsersToCorporateOnly } = useSubUserManagement();
  const {
    currentUser: { firstName, lastName, isSubUser, isMain, packageInfo, unreadAlertsCount, permanentRole },
  } = useAppSelector(selectUsers);
  const { isUserInForm } = useAppSelector(selectConfirmModal);
  const publicSettingsState = useAppSelector(selectPublicSettings);
  const publicSettings = publicSettingsState.settings;
  const PATH_WITHOUT_HEADER = ['/faq', '/about-us'];
  const disableHeader = PATH_WITHOUT_HEADER.includes(pathname);
  const logoUrl = publicSettings.branding?.logoUrl || AfrilandLogo;
  const userFullName = `${firstName || ''} ${lastName || ''}`;

  const tabTitles = useMemo(() => {
    return selectTabs(isMain, isSubUser, permanentRole, packageInfo.freeOfCharge, restrictSubUsersToCorporateOnly);
  }, [isSubUser, isMain, packageInfo, permanentRole, restrictSubUsersToCorporateOnly]);

  const toogleModal = () => {
    setProfileMenuOpen(!profileMenuOpen);
  };

  const handleProfilePage = () => {
    if (isUserInForm) {
      dispatch(setConfirmPath('/profile'));
      dispatch(setShowConfirmLeave(true));
    } else {
      navigate('/profile');
    }
    toogleModal();
  };

  const profileActive = () => {
    return pathname === '/profile';
  };

  const onLogoutClick = () => {
    if (isUserInForm) {
      dispatch(setConfirmPath('/log-in'));
      dispatch(setShowConfirmLeave(true));
    } else {
      logout();
    }
  };

  const handleLogout = () => {
    onLogoutClick();
    toogleModal();
  };

  const logout = () => {
    signOutScope()
      .then((_resp: any) => {
        dispatch(logoutUser());
        navigate('/log-in');
      })
      .catch(err => {
        console.log(err.data.message);
      });
  };

  const handleLogoClick = () => {
    if (isUserInForm) {
      dispatch(setConfirmPath('/dashboard'));
      dispatch(setShowConfirmLeave(true));
    } else {
      navigate('/dashboard');
    }
  };

  if (disableHeader) return null;

  const roleLable = () => {
    if (isMain) {
      return t('nav.mainUser');
    } else if (isSubUser) {
      return t('nav.subUser');
    } else {
      return t('nav.accountHolder');
    }
  };

  const customLink = (href: string, label: string, query?: string) => {
    return (
      <NavItem
        title={t(`nav.${label}`)}
        path={href}
        id={label}
        onClick={() => (openMenu ? setOpenMenu(false) : setProfileMenuOpen(false))}
        query={query}
        key={label}
      />
    );
  };

  const isCardEnabled = () => {
    return isCardFeature;
  };

  const renderBurgerMenu = () => {
    return (
      <div className={clsx(styles.contextMenu, '-left-3.5 !top-[60px] w-[600px] max-sm:w-[300px]')}>
        <ContextModal isOpen={openMenu} onClose={() => setOpenMenu(false)} width='100%'>
          <div className='flex flex-col'>
            {customLink('dashboard', 'dashboard')}
            {customLink('my-accounts', 'myAccounts')}
            {customLink('money-transfers', 'moneyTransfers')}
            {customLink('payments', 'payments')}
            {customLink('transactions', 'transactions')}
            {isStatementsFeature && customLink('my-statements', 'myStatements')}
            {customLink('transactions-validation', 'transactionsValidation')}
            {customLink('transactions-pending', 'IbTransactions')}
            {/* IB1-329 : mêmes règles de feature-flag que le dropdown desktop "Services" (DropdownNavItem) */}
            {!!isLoansFeature && customLink('loans', 'loans')}
            {!!isExchangesFeature && customLink('exchanges', 'exchanges')}
            {!!isDepositsFeature && customLink('deposits', 'deposit')}
            {!!isSavingFeature && customLink('saving', 'saving')}
            {isCardEnabled() && customLink('cards', 'cards')}
            {isCheckbookFeature && customLink('checkbook', 'checkbook')}
            {customLink('tickets', 'tickets')}
            {/* {customLink('my-goals', 'myGoals'} */}
            {/* {customLink('tradings', 'tradings')} */}
            {/* {customLink('fundraising', 'fundraising')} */}
            {/* {customLink('automatic-savings', 'automaticSavings')} */}
          </div>
        </ContextModal>
      </div>
    );
  };

  const renderProfileMenu = () => {
    return (
      <div className={clsx(styles.contextMenu, 'left-0 !top-[80px] w-[320px] !p-0')}>
        <ContextModal isOpen={profileMenuOpen} onClose={toogleModal} width='100%'>
          <div className='flex flex-col p-1'>
            <div className='flex flex-col items-start pl-2 mb-1 pb-4 border-b border-[#DDDFE5]'>
              <p className='pb-1 text-wrap'>{userFullName}</p>
              <p className={clsx(styles.label, 'otherCaption')}>{roleLable()}</p>
            </div>
            {tabTitles.map(title => customLink(`profile?tab=${title}`, title, title))}
            <button
              className={clsx(styles.contextMenu_item, 'subtitle3 !text-[#DF4949] flex justify-between items-center')}
              onClick={handleLogout}
            >
              {t('nav.logOut')}
              <LogoutIcon className='text-[#DF4949]' />
            </button>
          </div>
        </ContextModal>
      </div>
    );
  };

  const isMoreThan99 = () => {
    return unreadAlertsCount > 99;
  };

  const renderAlertsButton = () => (
    <div className='relative'>
      <IconButton onClick={() => navigate('/alerts')}>
        {unreadAlertsCount > 0 && (
          <Tooltip title={isMoreThan99() && t('alerts.unread', { count: unreadAlertsCount })}>
            <span
              className='absolute top-1 transform translate-x-1/2 -translate-y-1/2 bg-red-500 text-white text-xs
                font-bold rounded-full h-5 w-5 flex items-center justify-center'
            >
              {isMoreThan99() ? '...' : unreadAlertsCount}
            </span>
          </Tooltip>
        )}
        <NotificationsNoneIcon />
      </IconButton>
    </div>
  );

  return (
    <>
      <Autologout />
      <Logout />
      <header className={styles.headerBlock} id='header'>
        <div className='flex items-center relative'>
          <Button
            onClick={() => setOpenMenu(true)}
            inputStyle={clsx(styles.menu, openMenu && '!border-[#44546F]')}
            id='burgerMenuBtn'
            icon={openMenu ? CloseIcon : MenuIcon}
          />
          <button onClick={handleLogoClick}>
            <img src={logoUrl} className={styles.logo} alt='Logo navigate to dashboard' />
          </button>
          {renderBurgerMenu()}
        </div>
        <nav className={styles.navbar}>
          <NavItem title={t('nav.dashboard')} path='dashboard' id='dashboard' />
          <NavItem title={t('nav.moneyTransfers')} path='money-transfers' id='moneyTransfers' />
          <NavItem title={t('nav.payments')} path='payments' id='payments' />
          <DropdownNavItem title='transactions' />
          <DropdownNavItem title='services' />
        </nav>
        <section className={styles.profileMenu}>
          {renderAlertsButton()}
          <div className='flex flex-col items-end ml-2 mr-4'>
            <Tooltip title={userFullName.length > 20 && userFullName}>
              <p>{slicedText(userFullName, 20)}</p>
            </Tooltip>
            <p className={clsx(styles.label, 'otherCaption')}>{roleLable()}</p>
          </div>
          <div className='relative z-[799]'>
            <button className={clsx(styles.imagesBlock, profileMenuOpen && styles.active)} onClick={toogleModal}>
              {profileMenuOpen ? <ExpandLessIcon className='text-[#44546F]' /> : <ExpandMoreIcon className='text-[#44546F]' />}
            </button>
            <div className={styles.contextMenu}>
              <ContextModal isOpen={profileMenuOpen} onClose={toogleModal} width='248px'>
                <button
                  className={clsx(styles.contextMenu_item, 'subtitle3 mb-1', profileActive() && styles.active)}
                  onClick={() => handleProfilePage()}
                >
                  {t('nav.profile')}
                </button>
                <button className={clsx(styles.contextMenu_item, 'subtitle3')} onClick={handleLogout}>
                  {t('nav.logOut')}
                </button>
              </ContextModal>
            </div>
          </div>
        </section>
        <div className={styles.profileMenu__section}>
          {renderAlertsButton()}
          <Button
            onClick={toogleModal}
            inputStyle={clsx(styles.profileMenu__button, profileMenuOpen && '!border-[#44546F]')}
            id='burgerMenuBtnLeft'
            icon={UserIcon}
          />
          {renderProfileMenu()}
        </div>
      </header>
    </>
  );
};
